// การ์ด KPI ของผู้ใช้ (ยอดของฉัน/แผนก/หัวหน้า/ธุรการ) — ใช้ในแผงขวาของ InsightTicker และ Insight Console (view-as)
// kpiParts = สรุป KPI เป็นท่อนสั้น ๆ สำหรับสไลด์แรกของ InsightTicker
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Paper, Typography, LinearProgress, Tooltip, Chip, Link } from '@mui/material';
import { money, percent } from './format';

function Card({ title, children, tone = 'default', minWidth = 200 }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: 1.5, py: 1, minWidth, flex: `1 0 ${minWidth}px`, borderRadius: 2,
        bgcolor: tone === 'kpi' ? 'rgba(25,118,210,0.04)' : 'background.paper',
        borderColor: tone === 'kpi' ? 'primary.light' : 'divider',
        scrollSnapAlign: 'start',
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={600}>{title}</Typography>
      {children}
    </Paper>
  );
}

function Big({ children }) {
  return <Typography variant="h6" fontWeight={700} lineHeight={1.3}>{children}</Typography>;
}

function TargetProgress({ mtd, target }) {
  const pct = percent(mtd, target);
  if (pct === null) return <Typography variant="caption" color="text.secondary">ยังไม่มีเป้าเดือนนี้</Typography>;
  return (
    <Box sx={{ mt: 0.5 }}>
      <LinearProgress variant="determinate" value={Math.min(pct, 100)} sx={{ height: 6, borderRadius: 3 }} />
      <Typography variant="caption" color="text.secondary">{pct}% ของเป้า {money(target)}</Typography>
    </Box>
  );
}

function DeptLines({ dept, workdaysLeft }) {
  return (
    <>
      <Big>{money(dept.mtd)}</Big>
      <TargetProgress mtd={dept.mtd} target={dept.target} />
      {dept.projection != null && (
        <Typography variant="caption" display="block" color="text.secondary">
          น่าจะจบเดือนที่ประมาณ {money(dept.projection)}
        </Typography>
      )}
      {dept.perWorkdayNeeded != null && dept.perWorkdayNeeded > 0 && (
        <Typography variant="caption" display="block" color="text.secondary">
          ต้องทำอีกวันละ {money(dept.perWorkdayNeeded)} (เหลือ {workdaysLeft} วันทำการ)
        </Typography>
      )}
    </>
  );
}

function sectionCards(section, kpi) {
  const left = kpi.calendar?.workdaysLeft;
  if (section.persona === 'sales_member') {
    return [
      <Card key="own" title="ยอดของฉัน" tone="kpi">
        <Big>{money(section.own.today)} <Typography component="span" variant="caption" color="text.secondary">วันนี้</Typography></Big>
        <Typography variant="caption" display="block" color="text.secondary">
          สัปดาห์นี้ {money(section.own.week)} · เดือนนี้ {money(section.own.mtd)}
        </Typography>
      </Card>,
      <Card key="dept" title={`แผนก${section.dept.label || ''} (เดือนนี้)`} tone="kpi" minWidth={230}>
        <DeptLines dept={section.dept} workdaysLeft={left} />
      </Card>,
    ];
  }
  if (section.persona === 'sales_head') {
    return section.depts.map(d => (
      <Card key={`head-${d.key}`} title={`${d.label} (เดือนนี้)`} tone="kpi" minWidth={230}>
        <DeptLines dept={d} workdaysLeft={left} />
        {d.people?.length > 0 && (
          <Tooltip
            arrow
            title={
              <Box sx={{ py: 0.5 }}>
                {d.people.map(p => (
                  <Box key={p.saleCode} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <span>{p.name}</span><span>{money(p.mtd)}</span>
                  </Box>
                ))}
              </Box>
            }
          >
            <Chip size="small" label={`รายคน (${d.people.length})`} sx={{ mt: 0.5, cursor: 'help' }} />
          </Tooltip>
        )}
      </Card>
    ));
  }
  if (section.persona === 'salesadmin_member' || section.persona === 'salesadmin_head') {
    const d = section.docs;
    // นับเฉพาะเอกสารของเซลล์ที่ติดตามไว้ในหน้าตั้งค่าการแจ้งเตือน (ยังไม่ติดตามใคร = ไม่มีข้อมูล)
    const followed = section.followedSales;
    if (followed === 0) {
      return [
        <Card key={`docs-${section.persona}`} title="งานเอกสารวันนี้" tone="kpi" minWidth={230}>
          <Typography variant="body2" sx={{ mt: 0.5 }}>ยังไม่ได้เลือกเซลล์ที่ติดตาม</Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            insight ของธุรการแสดงเฉพาะเอกสารของเซลล์ที่คุณติดตาม —{' '}
            <Link component={RouterLink} to="/notification-settings">เลือกที่หน้าตั้งค่าการแจ้งเตือน</Link>
          </Typography>
        </Card>,
      ];
    }
    return [
      <Card
        key={`docs-${section.persona}`} tone="kpi" minWidth={230}
        title={`งานเอกสารวันนี้${followed != null ? ` · เซลล์ที่ติดตาม ${followed} คน` : ''}`}
      >
        <Big>เสนอราคา {d.qt_today} · สั่งขาย {d.so_today} · บิลขาย {d.bill_today}</Big>
        <Typography variant="caption" display="block" color="text.secondary">
          สั่งขายค้าง {d.so_open} ใบ{d.so_open_value != null ? ` (${money(d.so_open_value)})` : ''} · ค้างเกิน 1 วัน {d.so_open_over_1d} ใบ
        </Typography>
      </Card>,
    ];
  }
  return [];
}

// การ์ด KPI ชุดเดียวกับที่ผู้ใช้เห็น (ใช้ซ้ำใน Insight Console แบบ view-as)
export function KpiCards({ kpi }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {kpi.sections.flatMap(s => sectionCards(s, kpi))}
    </Box>
  );
}

// สรุป KPI เป็นท่อนสั้น ๆ (สไลด์แรกของ InsightTicker)
export function kpiParts(kpi) {
  const parts = [];
  for (const s of kpi.sections) {
    if (s.persona === 'sales_member') parts.push(`ยอดของฉันเดือนนี้ ${money(s.own.mtd)}`, `แผนก ${money(s.dept.mtd)}${s.dept.target ? ` / เป้า ${money(s.dept.target)}` : ''}`);
    if (s.persona === 'sales_head') s.depts.forEach(d => parts.push(`${d.label} ${money(d.mtd)}${d.target ? ` (${percent(d.mtd, d.target)}%)` : ''}`));
    if (s.persona.startsWith('salesadmin')) {
      parts.push(s.followedSales === 0 ? 'ยังไม่ได้เลือกเซลล์ที่ติดตาม (ตั้งค่าที่หน้าแจ้งเตือน)' : `สั่งขายค้าง ${s.docs.so_open} ใบ (เซลล์ที่ติดตาม)`);
    }
  }
  return [...new Set(parts)];
}
